import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { createForm, useCreateFormStore } from '@components/form';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useDuplicateJourney } from 'api/journeys/hooks';
import { DuplicateJourneyData } from 'api/journeys/requests';
import { JourneyType } from 'api/types';

export const useDuplicateJourneyModal = () => useModal(DuplicateJourneyModal);

const Form = createForm<DuplicateJourneyData>();

export const DuplicateJourneyModal = ({ journey }: { journey: JourneyType }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const duplicateMutation = useDuplicateJourney(journey.shortId);

  const handleSubmit = async () => {
    await duplicateMutation.mutateAsync(formStore.getState().values as DuplicateJourneyData);
    hideModal();
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Form store={formStore} onSubmit={handleSubmit}>
          <Form.TextField
            id="title"
            label={t('journeyName')}
            defaultValue={`${journey.title} [${t('copy')}]`}
          />
          <Stack space={1}>
            <Form.Checkbox
              id="includeTravellers"
              label={t('includeTravellers')}
              defaultValue={false}
              isDisabled={!journey.hasPublishedJourney}
              tooltip={!journey.hasPublishedJourney ? t('notPublishedDisabled') : undefined}
            />
            <Form.Checkbox id="includeTriggers" label={t('includeTriggers')} defaultValue={false} />
          </Stack>
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <CancelSaveButtons
          alwaysShow
          onCancel={hideModal}
          onSave={handleSubmit}
          translationsOverride={{ save: t('confirm') }}
          isDisabled={duplicateMutation.isLoading}
        />
      </Modal.Footer>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Duplicate journey',
    journeyName: 'Journey name',
    includeTravellers: 'Include travellers',
    includeTriggers: 'Include triggers',
    copy: 'copy',
    confirm: 'Duplicate journey',
    notPublishedDisabled: 'Cannot include travellers unless a journey is published',
  },
};
